import { ListItem, Stack } from "@mui/material";
import { motion } from "motion/react";
import { FC, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";

import {
  capitalize,
  innerItemEditStyles,
  isInnerItemEditingAtom,
  Log,
  ZERO_TIME_HHmm,
} from "@ryde/shared";

import {
  ColorIconButton,
  DropdownInputByControl,
  MultiSelectBoundInputByControl,
  TimeInputByControl,
} from "../../../controls";
import {
  emptyRouteSchedule,
  IRouteScheduleForm,
  IRouteScheduleItem,
} from "./table-route-schedule.models";

export const RouteScheduleItemEdit: FC<IRouteScheduleForm> = ({
  index,
  value,
  onCancel,
  setValue: setValueOuter,
  fieldName,
}) => {
  const {
    handleSubmit,
    control,
    formState: { isValid },
    trigger,
    setValue,
  } = useForm<Partial<IRouteScheduleItem>>({
    defaultValues: value,
    mode: "onChange",
  });
  const { t } = useTranslation();
  const setIsInnerItemEditing = useSetRecoilState(isInnerItemEditingAtom);

  useEffect(() => {
    const isAddMode =
      value &&
      (!value.time || value.time === emptyRouteSchedule.time) &&
      (!value.direction || value.direction === emptyRouteSchedule.direction) &&
      (!value.dayTypeIds || value.dayTypeIds.length === 0) &&
      (!value.dayGroupIds || value.dayGroupIds.length === 0);

    if (isAddMode) {
      setValue("time", ZERO_TIME_HHmm, {
        shouldDirty: true,
        shouldTouch: true,
      });
      trigger("time");
    }
  }, [setValue, value, trigger]);

  const updateValuesHandler = handleSubmit(
    data => {
      const targetField = `${fieldName || ""}.${index || 0}`;
      Log.debug(
        `Set value for route schedule item target field:  ${targetField}`,
        data,
      );
      setValueOuter?.(targetField, data, {
        shouldValidate: true,
        shouldDirty: true,
      });
      onCancel?.();
    },
    errors => {
      Log.debug("Route schedule item validation errors:", errors);
    },
  );

  const directions = useMemo(
    () => [
      { value: "A", label: t("selectListLabel.DirA") },
      { value: "B", label: t("selectListLabel.DirB") },
    ],
    [t],
  );

  const timeFormValidation = useMemo(
    () => ({
      type: "string" as const,
      rules: [
        {
          validation: "required" as const,
        },
      ],
    }),
    [],
  );

  const directionFormValidation = useMemo(
    () => ({
      type: "string" as const,
      rules: [
        {
          validation: "required" as const,
        },
      ],
    }),
    [],
  );

  return (
    <motion.div
      style={innerItemEditStyles}
      initial={{ scale: 0.97, opacity: 0, y: -8 }}
      animate={{ scale: 1, opacity: 1, y: 0 }}
      exit={{ scale: 0.97, opacity: 0, y: -8 }}
      transition={{
        duration: 0.55,
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        ease: [0.25, 0.1, 0.25, 1],
        opacity: { duration: 0.45 },
      }}
    >
      <ListItem
        alignItems="flex-start"
        data-e2e="route-schedule-item-edit"
        className="no-blur-item"
        secondaryAction={
          <>
            <ColorIconButton
              iconType="CHECK"
              edge="end"
              aria-label="save"
              disabled={!isValid}
              onClick={async () => {
                const isValidForm = await trigger();
                if (isValidForm) {
                  updateValuesHandler();
                }
              }}
            />

            <ColorIconButton
              iconType="CANCEL"
              edge="end"
              aria-label="cancel"
              onClick={() => {
                setIsInnerItemEditing(false);
                onCancel?.();
              }}
            />
          </>
        }
      >
        <Stack spacing={1} sx={{ width: "91%" }}>
          <TimeInputByControl
            name={`time`}
            control={control}
            formLabel={t("general.TIME")}
            formValidation={timeFormValidation}
          ></TimeInputByControl>

          <DropdownInputByControl
            name={`direction`}
            control={control}
            formLabel={t("general.DIRECTION")}
            formValidation={directionFormValidation}
            formAttributes={{
              selectItems: directions,
            }}
          ></DropdownInputByControl>

          <MultiSelectBoundInputByControl
            name={`dayTypeIds`}
            control={control}
            formLabel={capitalize(t("dayType.plural"))}
            formAttributes={{
              dropItemsSource: "dayType",
            }}
          ></MultiSelectBoundInputByControl>

          <MultiSelectBoundInputByControl
            name={`dayGroupIds`}
            control={control}
            formLabel={capitalize(t("dayGroup.plural"))}
            formAttributes={{
              dropItemsSource: "dayGroup",
            }}
          />
        </Stack>
      </ListItem>
    </motion.div>
  );
};
